import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Browser from "../classes/Browser";
import ToolTipWrapper from "./ToolTipWrapper";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import IconView from "./IconView";
import { BuildContext } from "../classes/BuildContext";

type _HelpUrlRefOnTap = (d3eState: LabelWithHelpRefs) => void;

export interface LabelWithHelpProps extends BaseUIProps {
  key?: string;
  label: string;
  helpUrl?: string;
  help?: string;
}
/// To store state data for LabelWithHelp
class LabelWithHelpRefs {
  public helpUrlRef: HelpUrlRefState = new HelpUrlRefState();
}

interface HelpUrlRefWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: LabelWithHelpRefs;
  _openHelpUrlHandler?: _HelpUrlRefOnTap;
}

class HelpUrlRefState extends ObjectObservable {
  private _hover: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _HelpUrlRefWithState extends ObservableComponent<HelpUrlRefWithStateProps> {
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: HelpUrlRefWithStateProps) {
    super(props);

    this.initState();
  }
  public get helpUrlRef(): HelpUrlRefState {
    return this.props.d3eState.helpUrlRef;
  }
  public get d3eState(): LabelWithHelpRefs {
    return this.props.d3eState;
  }
  public get _openHelpUrlHandler(): _HelpUrlRefOnTap {
    return this.props._openHelpUrlHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("helpUrlRef", null, this.helpUrlRef);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["helpUrlRef", "helpUrlRef.hover"], this.rebuild);
  }
  public helpUrlRefOnEnter(event): void {
    return this.helpUrlRef.setHover(true);
  }
  public helpUrlRefOnExit(event): void {
    return this.helpUrlRef.setHover(false);
  }
  public dispose(): void {
    this.helpUrlRef.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return IconView({
      icon: MaterialIcons.info_outline,
      size: 13,
      color: cStyle.c4,
      onEnter: (event) => {
        this.helpUrlRefOnEnter(event);
      },
      onExit: (event) => {
        this.helpUrlRefOnExit(event);
      },
      onTap: (e) => {
        e.stopPropagation();

        this._openHelpUrlHandler(this.d3eState);
      },
      className: "x735",
    });
  }
}
function HelpUrlRefWithState(props: HelpUrlRefWithStateProps) {
  return React.createElement(_HelpUrlRefWithState, props);
}

class _LabelWithHelpState extends ObservableComponent<LabelWithHelpProps> {
  static defaultProps = { label: "", helpUrl: "", help: "" };
  d3eState: LabelWithHelpRefs = new LabelWithHelpRefs();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: LabelWithHelpProps) {
    super(props);

    this.initState();
  }
  public get label(): string {
    return this.props.label;
  }
  public get helpUrl(): string {
    return this.props.helpUrl;
  }
  public get help(): string {
    return this.props.help;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["help", "helpUrl", "label"], this.rebuild);
  }
  public componentDidUpdate(prevProps: LabelWithHelpProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.label !== this.props.label) {
      this.fire("label", this);
    }

    if (prevProps.helpUrl !== this.props.helpUrl) {
      this.fire("helpUrl", this);
    }

    if (prevProps.help !== this.props.help) {
      this.fire("help", this);
    }
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Row({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      children: [
        TextView({ data: this.label, className: "xdf7", key: "0" }),
        (this.help !== null && this.help.isNotEmpty) ||
        (this.helpUrl !== null && this.helpUrl.isNotEmpty)
          ? ui.Center({
              child: ToolTipWrapper({
                message:
                  this.help === null && this.help.isEmpty
                    ? "Information"
                    : this.help,
                child: ui.Container({
                  alignment: ui.Alignment.topLeft,
                  child: HelpUrlRefWithState({
                    d3eState: this.d3eState,
                    _openHelpUrlHandler: this.openHelpUrlHandler,
                  }),
                  className: "x4b4",
                }),
              }),
            })
          : [],
      ],
      className: ui.join(this.props.className, "LabelWithHelp"),
      ...copyBaseUIProps(this.props),
    });
  }
  public openHelpUrlHandler = (d3eState: LabelWithHelpRefs): void => {
    if (this.helpUrl !== null && this.helpUrl !== "") {
      Browser.open(this.helpUrl);
    }
  };
  public get helpUrlRef() {
    return this.d3eState.helpUrlRef;
  }
}
export default function LabelWithHelp(props: LabelWithHelpProps) {
  return React.createElement(_LabelWithHelpState, {
    ..._LabelWithHelpState.defaultProps,
    ...props,
  });
}
